import React, { useEffect, useState } from "react";
import { API_BASE } from "../api/config";
import LocationSelect from "../components/LocationSelect";
import "./StockAdjustmentApprovalPanel.css";

function formatQty(value) {
  return Number(value || 0).toLocaleString();
}

function shortId(value) {
  return String(value || "").slice(0, 8);
}

export default function StockAdjustmentApprovalPanel() {
  const [status, setStatus] = useState("PENDING");
  const [location, setLocation] = useState("ALL");
  const [brand, setBrand] = useState("ALL");
  const [productCode, setProductCode] = useState("");
  const [rows, setRows] = useState([]);
  const [noteMap, setNoteMap] = useState({});
  const [loading, setLoading] = useState(false);
  const [savingId, setSavingId] = useState("");
  const [error, setError] = useState("");
  const [successMsg, setSuccessMsg] = useState("");

  async function loadRequests() {
    try {
      setLoading(true);
      setError("");
      setSuccessMsg("");

      const query = new URLSearchParams();
      query.set("status", status || "PENDING");
      query.set("locationCode", location || "ALL");
      query.set("brand", brand || "ALL");

      if (productCode.trim()) {
        query.set("productCode", productCode.trim());
      }

      const response = await fetch(`${API_BASE}/api/erp/stock-adjustment-requests?${query.toString()}`);
      const data = await response.json();

      if (!response.ok || data.success === false) {
        throw new Error(data.message || "Failed to load requests");
      }

      const nextRows = Array.isArray(data.rows) ? data.rows : [];
      setRows(nextRows);

      const nextNotes = {};
      nextRows.forEach((row) => {
        nextNotes[row.id] = row.decisionNote || "";
      });
      setNoteMap(nextNotes);
    } catch (err) {
      setError(err.message);
      setRows([]);
    } finally {
      setLoading(false);
    }
  }

  async function approveRequest(row) {
    try {
      setSavingId(row.id);
      setError("");
      setSuccessMsg("");

      const response = await fetch(`${API_BASE}/api/erp/stock-adjustment-requests/${row.id}/approve`, {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          approvedBy: "ADMIN",
          decisionNote: noteMap[row.id] || "Approved for later stock correction",
        }),
      });

      const data = await response.json();

      if (!response.ok || data.success === false) {
        throw new Error(data.message || "Approve failed");
      }

      setSuccessMsg(`Approved: ${row.locationCode} / ${row.productCode}`);
      await loadRequests();
    } catch (err) {
      setError(err.message);
    } finally {
      setSavingId("");
    }
  }

  async function applyRequest(row) {
    try {
      setSavingId(row.id);
      setError("");
      setSuccessMsg("");

      const response = await fetch(`${API_BASE}/api/erp/stock-adjustment-requests/${row.id}/apply`, {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          appliedBy: "ADMIN",
          applyNote: noteMap[row.id] || "Applied ERP stock correction",
        }),
      });

      const data = await response.json();

      if (!response.ok || data.success === false) {
        throw new Error(data.message || "Apply failed");
      }

      setSuccessMsg(`Applied: ${row.locationCode} / ${row.productCode} / ${data.direction} ${data.qty}`);
      await loadRequests();
    } catch (err) {
      setError(err.message);
    } finally {
      setSavingId("");
    }
  }

  async function rejectRequest(row) {
    try {
      setSavingId(row.id);
      setError("");
      setSuccessMsg("");

      const response = await fetch(`${API_BASE}/api/erp/stock-adjustment-requests/${row.id}/reject`, {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          rejectedBy: "ADMIN",
          decisionNote: noteMap[row.id] || "Rejected by admin",
        }),
      });

      const data = await response.json();

      if (!response.ok || data.success === false) {
        throw new Error(data.message || "Reject failed");
      }

      setSuccessMsg(`Rejected: ${row.locationCode} / ${row.productCode}`);
      await loadRequests();
    } catch (err) {
      setError(err.message);
    } finally {
      setSavingId("");
    }
  }

  useEffect(() => {
    loadRequests();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status, location, brand]);

  return (
    <div className="stockApprovalPanel">
      <div className="stockApprovalPanel__head">
        <div>
          <div className="stockApprovalPanel__tag">APPROVAL CONTROL</div>
          <h3>Stock Adjustment Approval Panel</h3>
          <p>
            Fix Request တွေကို Approve / Reject လုပ်ရန်။ ဒီအဆင့်မှာ stock qty နှင့် Firebase ကို မပြင်သေးပါ။
          </p>
        </div>

        <button type="button" onClick={loadRequests} disabled={loading}>
          {loading ? "Loading..." : "Refresh"}
        </button>
      </div>

      <div className="stockApprovalPanel__filters">
        <label>
          <span>Status</span>
          <select value={status} onChange={(e) => setStatus(e.target.value)}>
            <option value="PENDING">PENDING</option>
            <option value="APPROVED">APPROVED</option>
            <option value="REJECTED">REJECTED</option>
            <option value="APPLIED">APPLIED</option>
            <option value="ALL">ALL</option>
          </select>
        </label>

        <label>
          <span>Location</span>
          <LocationSelect
            value={location}
            onChange={setLocation}
            type="ALL"
            includeAll={true}
            allLabel="All Locations"
          />
        </label>

        <label>
          <span>Brand</span>
          <select value={brand} onChange={(e) => setBrand(e.target.value)}>
            <option value="ALL">ALL</option>
            <option value="PRETTYFIT">PRETTYFIT</option>
            <option value="HANGTEN">HANGTEN</option>
          </select>
        </label>

        <label>
          <span>Search Code</span>
          <input
            value={productCode}
            onChange={(e) => setProductCode(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") loadRequests();
            }}
            placeholder="Product code..."
          />
        </label>

        <button type="button" onClick={loadRequests} disabled={loading}>
          Search
        </button>
      </div>

      {error && <div className="stockApprovalPanel__error">{error}</div>}
      {successMsg && <div className="stockApprovalPanel__success">{successMsg}</div>}

      <div className="stockApprovalPanel__summary">
        Requests: <b>{rows.length}</b> / Status: <b>{status}</b>
      </div>

      <div className="stockApprovalTableWrap">
        <table className="stockApprovalTable">
          <thead>
            <tr>
              <th>ID</th>
              <th>Status</th>
              <th>Location</th>
              <th>Brand</th>
              <th>Code</th>
              <th>Color</th>
              <th>Size</th>
              <th>Current</th>
              <th>Request Qty</th>
              <th>Diff</th>
              <th>Reason</th>
              <th>Decision Note</th>
              <th>Requested By</th>
              <th>Created</th>
              <th>Action</th>
            </tr>
          </thead>

          <tbody>
            {rows.length === 0 ? (
              <tr>
                <td colSpan="15" className="stockApprovalTable__empty">
                  No adjustment requests.
                </td>
              </tr>
            ) : (
              rows.map((row) => (
                <tr key={row.id}>
                  <td>{shortId(row.id)}</td>
                  <td>
                    <span className={`stockApprovalStatus stockApprovalStatus--${String(row.status).toLowerCase()}`}>
                      {row.status}
                    </span>
                  </td>
                  <td>{row.locationCode}</td>
                  <td>{row.brand || "-"}</td>
                  <td className="stockApprovalTable__code">{row.productCode}</td>
                  <td>{row.color || "-"}</td>
                  <td>{row.size || "-"}</td>
                  <td className="stockApprovalTable__current">{formatQty(row.currentQty)}</td>
                  <td className="stockApprovalTable__request">{formatQty(row.requestedQty)}</td>
                  <td className={Number(row.diffQty || 0) >= 0 ? "stockApprovalTable__diffIn" : "stockApprovalTable__diffOut"}>
                    {formatQty(row.diffQty)}
                  </td>
                  <td className="stockApprovalTable__reason">{row.reason || "-"}</td>
                  <td>
                    <input
                      className="stockApprovalTable__note"
                      value={noteMap[row.id] || ""}
                      onChange={(e) =>
                        setNoteMap((prev) => ({
                          ...prev,
                          [row.id]: e.target.value,
                        }))
                      }
                      placeholder="Decision note..."
                      disabled={row.status !== "PENDING"}
                    />
                  </td>
                  <td>{row.requestedBy || "-"}</td>
                  <td>{row.createdAt ? new Date(row.createdAt).toLocaleString() : "-"}</td>
                  <td>
                    {row.status === "PENDING" ? (
                      <div className="stockApprovalActions">
                        <button
                          type="button"
                          className="stockApprovalActions__approve"
                          onClick={() => approveRequest(row)}
                          disabled={savingId === row.id}
                        >
                          Approve
                        </button>
                        <button
                          type="button"
                          className="stockApprovalActions__reject"
                          onClick={() => rejectRequest(row)}
                          disabled={savingId === row.id}
                        >
                          Reject
                        </button>
                      </div>
                    ) : row.status === "APPROVED" ? (
                      <button
                        type="button"
                        className="stockApprovalActions__apply"
                        onClick={() => applyRequest(row)}
                        disabled={savingId === row.id}
                      >
                        {savingId === row.id ? "Applying..." : "Apply"}
                      </button>
                    ) : (
                      <span className="stockApprovalTable__done">Done</span>
                    )}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
